import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Linking,
} from "react-native";
import React, { useState, useEffect } from "react";
import Colors from "../../Resources/Color";
import GlobalStyles from "../../Resources/Styles";
import Fonts from "../../Resources/Fonts";
import { Feather, Ionicons } from "@expo/vector-icons";
import { getAuth, signOut } from "firebase/auth";
import { useScreenDimensions, useDevice } from "../_layout";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import CustomAlert from "../Components/CustomAlert";
import Tooltip from "../Components/Tooltip";
import db from "../Firestore/FirestoreConfig";
import { storage } from "../Firestore/FirestoreConfig";
import { doc, updateDoc, deleteDoc, getDoc } from "firebase/firestore";
import {
  ref,
  getDownloadURL,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import LogoImage from "../Components/LogoImage";
import CustomActivityIndicator from "../Components/CustomActivityIndicator";
import NoTranslateText from "../Components/NoTranslateText";
import { getFormattedVersion } from "../utils/version";
import BlockViewRevertModal from "../ClientViews/Planification/BlockViewRevertModal";

// Constants
const MAX_SIZE_IN_BYTES = 5 * 1024 * 1024; // 5MB limit
const STRIPE_BILLING_URL =
  "https://billing.stripe.com/p/login/9AQ3fW6andcNgne000";
const INSTAGRAM_URL = "https://www.instagram.com/efortapp/";

const ALERT_CONFIGS = {
  resetExercises: {
    title: "resetExercisesAlertTitle-string",
    text: "resetExercisesAlertText-string",
    cta: "resetButton-string",
  },
  resetAttributes: {
    title: "resetAttributesAlertTitle-string",
    text: "resetAttributesAlertText-string",
    cta: "resetButton-string",
  },
  deleteAccount: {
    title: "deleteAccountAlertTitle-string",
    text: "deleteAccountAlertText-string",
    cta: "delete-string",
  },
  deleteLogo: {
    title: "deleteLogoAlertTitle-string",
    text: "deleteLogoAlertText-string",
    cta: "delete-string",
  },
  uploadError: {
    title: "uploadErrorTitle-string",
    cta: "change-string",
  },
};

const CoachSettings = observer(({ coach }) => {
  const auth = getAuth();
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();
  const { screenSize } = useScreenDimensions();

  // Alert states
  const [showingResetExercises, setShowingResetExercises] = useState(false);
  const [showingResetAttributes, setShowingResetAttributes] = useState(false);
  const [showingDeleteAccount, setShowingDeleteAccount] = useState(false);
  const [showingDeleteLogoWarning, setShowingDeleteLogoWarning] =
    useState(false);
  const [showingUploadImageError, setShowingUploadImageError] = useState(false);
  const [showingLegacyViewModal, setShowingLegacyViewModal] = useState(false);
  const [uploadImageErrorText, setUploadImageErrorText] = useState(
    "uploadFailedText-string"
  );

  // Initialize coach settings on mount
  useEffect(() => {
    const settings = {
      name: coach.name,
      surname: coach.surname,
      exercise_unit: coach.exerciseUnit,
      rir_or_rpe: coach.rirOrRpe,
      coach_language: coach.coachLanguage,
      show_athlete_exercise_stats: coach.showAthleteExerciseStats ?? false,
      show_set_priority: coach.showSetPriority ?? true,
      default_block_view: coach.defaultBlockView ?? "workouts-string",
    };
    Object.entries(settings).forEach(([key, value]) => {
      coach.setSettings(key, value);
    });
    console.log("settings initialized", coach.coachSettings);
  }, []);

  const isUsingLegacyPlanView = Boolean(coach?.useLegacyPlanView);
  const shouldShowLegacyBlockViewSettings = Boolean(
    coach?.isLegacyCoachForBlockView?.()
  );

  // Helper functions
  const hasSettingsChanged = () => {
    return (
      coach.coachSettings["name"] !== coach.name ||
      coach.coachSettings["surname"] !== coach.surname ||
      coach.coachSettings["exercise_unit"] !== coach.exerciseUnit ||
      coach.coachSettings["rir_or_rpe"] !== coach.rirOrRpe ||
      coach.coachSettings["coach_language"] !== coach.coachLanguage ||
      coach.coachSettings["show_athlete_exercise_stats"] !==
        (coach.showAthleteExerciseStats ?? false) ||
      coach.coachSettings["show_set_priority"] !==
        (coach.showSetPriority ?? true) ||
      coach.coachSettings["default_block_view"] !==
        (coach.defaultBlockView ?? "workouts-string")
    );
  };

  const openURL = async (url) => {
    try {
      await Linking.openURL(url);
    } catch (error) {
      console.error(`Failed to open URL: ${url}`, error);
    }
  };

  const getDocsURL = () => {
    const languagePrefix = i18n.language === "es" ? "/es" : "";
    return `https://app.efortcoach.com/docs${languagePrefix}/program/overview`;
  };

  const handleEnableLegacyView = () => {
    setShowingLegacyViewModal(true);
  };

  const handleConfirmEnableLegacyView = async () => {
    if (coach) {
      await coach.setUseLegacyPlanView(true);
    }
    setShowingLegacyViewModal(false);
  };

  const handleDisableLegacyView = async () => {
    if (coach) {
      await coach.setUseLegacyPlanView(false);
    }
  };

  const LegacyViewPicker = () => {
    const [isLegacyHovered, setIsLegacyHovered] = useState(false);
    const [isNewHovered, setIsNewHovered] = useState(false);

    const options = [
      {
        key: "legacy",
        label: t("legacyBlockViewOptionLegacy-string"),
        value: true,
        onPress: handleEnableLegacyView,
      },
      {
        key: "new",
        label: t("legacyBlockViewOptionNew-string"),
        value: false,
        onPress: handleDisableLegacyView,
      },
    ];

    return (
      <View
        style={[
          styles.containerPicker,
          { backgroundColor: Colors.backgroundColor, borderRadius: 12 },
        ]}
      >
        <View style={styles.segmentedControl}>
          {options.map((option, index) => {
            const isSelected = isUsingLegacyPlanView === option.value;
            const isHovered =
              option.value === true ? isLegacyHovered : isNewHovered;

            return (
              <TouchableOpacity
                key={option.key}
                style={[
                  styles.segment,
                  isSelected && styles.selectedSegment,
                  { width: screenSize === "small" ? 60 : 116 },
                ]}
                onPress={() => {
                  if (!isSelected) {
                    option.onPress();
                  }
                }}
                onMouseEnter={() => {
                  if (!isMobile) {
                    option.value === true
                      ? setIsLegacyHovered(true)
                      : setIsNewHovered(true);
                  }
                }}
                onMouseLeave={() => {
                  option.value === true
                    ? setIsLegacyHovered(false)
                    : setIsNewHovered(false);
                }}
              >
                <Text
                  style={{
                    color: isSelected ? Colors.whiteColor : Colors.grayColor,
                    fontWeight: isHovered ? "500" : "normal",
                  }}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  minimumFontScale={0.7}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // API functions
  const deleteAllClientsAndCoach = async () => {
    const coachClients = coach.clients;

    try {
      for (const client of coachClients) {
        await client.deleteClient();
      }

      const coachDocRef = doc(db, "coaches", coach.id);
      await deleteDoc(coachDocRef);
      await signOut(auth);

      console.log("Coach and all associated clients successfully deleted.");
    } catch (error) {
      console.error("Error during the deletion process:", error);
    }
  };

  const logout = () => {
    if (!coach.isOffline) {
      signOut(auth)
        .then(() => {
          coach.resetCoach();
          coach.setProperty("initialLoading", true);
        })
        .catch((error) => {
          console.error("error logout", error);
        });
    }
  };

  const saveCoachProfile = async () => {
    if (coach.isOffline) return;

    let hasChanges = false;
    let hasToUpdateApplicationForm = false;

    const settingsChecks = [
      { key: "coach_language", prop: "coachLanguage", updateForm: true },
      { key: "name", prop: "name", updateForm: true },
      { key: "surname", prop: "surname", updateForm: true },
      { key: "exercise_unit", prop: "exerciseUnit" },
      { key: "rir_or_rpe", prop: "rirOrRpe" },
      {
        key: "show_athlete_exercise_stats",
        prop: "showAthleteExerciseStats",
        defaultValue: false,
      },
      {
        key: "show_set_priority",
        prop: "showSetPriority",
        defaultValue: false,
      },
      {
        key: "default_block_view",
        prop: "defaultBlockView",
        defaultValue: "workouts-string",
      },
    ];

    settingsChecks.forEach(({ key, prop, updateForm, defaultValue }) => {
      const currentValue =
        defaultValue !== undefined ? coach[prop] ?? defaultValue : coach[prop];
      if (coach.coachSettings[key] !== currentValue) {
        if (key === "coach_language") {
          coach.translate(coach.coachSettings[key], t);
        }
        coach.setProperty(prop, coach.coachSettings[key]);
        hasChanges = true;
        if (updateForm) hasToUpdateApplicationForm = true;
      }
    });

    if (hasChanges) {
      // Filter out show_athlete_exercise_stats, show_set_priority, and default_block_view from Firestore upload
      const {
        show_athlete_exercise_stats,
        show_set_priority,
        default_block_view,
        ...settingsToSave
      } = coach.coachSettings;
      coach.saveCoachProfile(settingsToSave);

      // Upload public coach data
      try {
        const publicCoachData = {
          id: coach.id,
          name: coach.coachSettings["name"],
          surname: coach.coachSettings["surname"],
          email: coach.email,
          rir_or_rpe: coach.coachSettings["rir_or_rpe"],
          exercise_unit: coach.coachSettings["exercise_unit"],
          show_set_priority: coach.coachSettings["show_set_priority"] ?? false,
          show_athlete_exercise_stats:
            coach.coachSettings["show_athlete_exercise_stats"] ?? false,
          default_block_view:
            coach.coachSettings["default_block_view"] ?? "workouts-string",
          language: coach.coachSettings["coach_language"],
        };

        await coach.updateCoachPublicProfile(publicCoachData);

        console.log("Public coach data updated successfully.");
      } catch (error) {
        console.error("Error updating public coach data:", error);
      }
    }

    if (hasToUpdateApplicationForm) {
      try {
        const coachingFormDocRef = doc(
          db,
          "coaching_application_forms",
          coach.id
        );
        const docSnapshot = await getDoc(coachingFormDocRef);

        if (docSnapshot.exists()) {
          await updateDoc(coachingFormDocRef, {
            coach_language: coach.coachLanguage,
            coach_name: coach.name,
            coach_surname: coach.surname,
          });
        } else {
          console.log(
            "Coaching form document does not exist, skipping update."
          );
        }
      } catch (error) {
        console.error("Error updating coaching form:", error);
      }
    }
  };

  const resetPredefinedExercises = async () => {
    const filteredCustomExercises = coach.customPredefinedExercises.filter(
      (exercise) => !exercise.name.includes("-string")
    );
    const predefinedExercises = coach.getPredefinedArray();

    coach.customPredefinedExercises = [
      ...filteredCustomExercises,
      ...predefinedExercises,
    ];

    const exercisesArray = coach.customPredefinedExercises.map(
      (exercise, index) => ({
        id: exercise.id.toString().toLowerCase(),
        name: exercise.name,
        notes: exercise.notes,
        order: index,
        original_name: exercise.originalName,
        type: exercise.type,
        link: exercise.link,
        is_predefined: true,
      })
    );

    const userCoachRef = doc(db, "coaches", coach.id);
    try {
      await updateDoc(userCoachRef, {
        custom_predefined_exercises: exercisesArray,
      });
      console.log("Custom predefined exercises reset and updated.");
    } catch (error) {
      console.error("Error updating custom predefined exercises:", error);
    }
  };

  const resetPredefinedAttributes = async () => {
    const filteredCustomAttributes = coach.customPredefinedAttributes.filter(
      (attribute) => !attribute.name.includes("-string")
    );
    const predefinedAttributes = coach.getPredefinedAttributes();

    coach.customPredefinedAttributes = [
      ...filteredCustomAttributes,
      ...predefinedAttributes,
    ];

    const attributesArray = coach.customPredefinedAttributes.map(
      (attribute, index) => ({
        id: attribute.id.toString().toLowerCase(),
        name: attribute.name,
        title: attribute.title,
        subtitle: attribute.subtitle,
        order: index,
        is_predefined: true,
      })
    );

    const userCoachRef = doc(db, "coaches", coach.id);
    try {
      await updateDoc(userCoachRef, {
        custom_predefined_attributes: attributesArray,
      });
      console.log("Custom predefined attributes reset and updated.");
    } catch (error) {
      console.error("Error updating custom predefined attributes:", error);
    }
  };

  const deleteLogo = async () => {
    try {
      const storageRef = ref(storage, `coach_logos/${coach.id}`);
      await deleteObject(storageRef);

      coach.setProperty("logoUrl", null);
      coach.setProperty("logoAspectRatioWH", null);
      coach.setProperty("logoAspectRatioHW", null);
      console.log("Logo deleted successfully!");
    } catch (error) {
      console.error("Error deleting logo:", error);
    }
  };

  // Reusable Components
  const HoverableButton = ({ onPress, style, children, disabled = false }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        onPress={onPress}
        style={[style, { opacity: disabled ? 0.5 : isHovered ? 0.7 : 1 }]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={disabled}
      >
        {typeof children === "function" ? children(isHovered) : children}
      </TouchableOpacity>
    );
  };

  const SettingsDivider = () => <View style={styles.divider} />;

  const SettingsSection = ({ title, children, topMargin = true }) => (
    <>
      <Text
        style={[
          Fonts.medium20,
          {
            marginBottom: screenSize === "small" ? 8 : 12,
            paddingTop: topMargin ? (screenSize === "small" ? 16 : 24) : 0,
          },
        ]}
      >
        {t(title)}
      </Text>
      <View style={styles.settingsCard}>{children}</View>
    </>
  );

  const SettingsRow = ({
    label,
    children,
    hasDescription = false,
    description,
    isLast = false,
    tooltip,
    noTranslateDescription = false, // New prop to control translation
  }) => (
    <View
      style={[
        GlobalStyles.HStack,
        { alignItems: "center", marginBottom: isLast ? 0 : 20 },
      ]}
    >
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <View
          style={{
            // minWidth: "40%",
            width: screenSize === "small" ? 130 : 280,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <View style={{ flexShrink: 1 }}>
            <Text style={[Fonts.regular14]}>{label}</Text>
            {hasDescription &&
              (noTranslateDescription ? (
                <NoTranslateText
                  style={[
                    Fonts.regular14,
                    {
                      color: Colors.grayColor,
                      marginTop: 12,
                    },
                  ]}
                >
                  {description}
                </NoTranslateText>
              ) : (
                <Text
                  style={[
                    Fonts.regular14,
                    {
                      color: Colors.grayColor,
                      marginTop: 12,
                    },
                  ]}
                >
                  {description}
                </Text>
              ))}
          </View>

          {tooltip && (
            <Tooltip content={tooltip} placement="top" maxWidth={300}>
              <View style={[styles.tooltipIconContainer, {}]}>
                <Ionicons
                  name="information-circle-outline"
                  size={18}
                  color={Colors.grayColor}
                />
              </View>
            </Tooltip>
          )}
        </View>
      </View>
      <View
        style={[
          GlobalStyles.HStack,
          {
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "space-around",
            flex: 1,
          },
        ]}
      >
        {children}
      </View>
    </View>
  );

  const BinaryPicker = observer(
    ({
      firestoreProperty,
      option1Text,
      option2Text,
      getValue = (index) => index === 0,
    }) => {
      const handleOptionChange = (newValue) => {
        if (!coach.isLoading) {
          coach.setSettings(firestoreProperty, newValue);
        }
      };

      const PickerButton = observer(({ option, index }) => {
        const [isHovered, setIsHovered] = useState(false);
        const optionValue = getValue(index);
        const isSelected =
          coach.coachSettings[firestoreProperty] === optionValue;

        return (
          <TouchableOpacity
            style={[
              styles.segment,
              isSelected && styles.selectedSegment,
              { width: screenSize === "small" ? 60 : 116 },
            ]}
            onPress={() => handleOptionChange(getValue(index))}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Text
              style={{
                color: isSelected ? Colors.whiteColor : Colors.grayColor,
                fontWeight: isHovered ? "500" : "normal",
              }}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.7}
            >
              {option}
            </Text>
          </TouchableOpacity>
        );
      });

      return (
        <View
          style={[
            styles.containerPicker,
            { backgroundColor: Colors.backgroundColor, borderRadius: 12 },
          ]}
        >
          <View style={styles.segmentedControl}>
            {[option1Text, option2Text].map((option, index) => (
              <PickerButton option={option} index={index} key={index} />
            ))}
          </View>
        </View>
      );
    }
  );

  const TextName = observer(({ firestoreProperty, placeholder }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        style={[
          styles.textInputContainer,
          {
            backgroundColor: "transparent",
            width: "45%",
            borderColor: isHovered ? "rgba(0, 0, 0, 0.5)" : Colors.borderColor,
          },
        ]}
      >
        <TextInput
          value={coach.coachSettings[firestoreProperty] || ""}
          onChangeText={(text) => coach.setSettings(firestoreProperty, text)}
          style={[
            styles.textInputStyle,
            { color: "black", outline: "none", width: "100%" },
          ]}
          placeholder={t(placeholder)}
          placeholderTextColor={Colors.grayColor}
          maxLength={40}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
      </View>
    );
  });

  const UnmutableTextName = ({ property, placeholder }) => (
    <View
      style={[
        styles.textInputContainer,
        { backgroundColor: "transparent", width: "45%" },
      ]}
    >
      <Text
        style={[
          styles.textInputStyle,
          { color: Colors.grayColor, outline: "none", width: "100%" },
        ]}
        numberOfLines={1}
      >
        {coach.getProperty(property) || placeholder}
      </Text>
    </View>
  );

  const ActionButton = ({ onPress, label, disabled = false }) => (
    <HoverableButton
      onPress={onPress}
      style={styles.resetButton}
      disabled={disabled || coach.isOffline}
    >
      <Text style={{ color: Colors.whiteColor }}>{t(label)}</Text>
    </HoverableButton>
  );

  const ImageUploadComponent = observer(() => {
    const [isHovered, setIsHovered] = useState(false);

    const handleUpload = async (selectedImage) => {
      if (!selectedImage || !coach.id) return;

      try {
        const storageRef = ref(storage, `coach_logos/${coach.id}`);
        await uploadBytes(storageRef, selectedImage);
        const downloadURL = await getDownloadURL(storageRef);

        coach.setProperty("isLogoUploading", false);
        coach.setProperty("logoUrl", downloadURL);
        console.log("Upload successful!");
      } catch (error) {
        console.error("Error during image upload:", error);
        setUploadImageErrorText("uploadFailedText-string");
        setShowingUploadImageError(true);
      }
    };

    const handleImageChange = (event) => {
      if (event.target.files && event.target.files[0]) {
        coach.setProperty("isLogoUploading", true);
        const selectedImage = event.target.files[0];

        if (!selectedImage.type.startsWith("image/")) {
          setUploadImageErrorText("selectImageFileText-string");
          setShowingUploadImageError(true);
          coach.setProperty("isLogoUploading", false);
          return;
        }

        if (selectedImage.size > MAX_SIZE_IN_BYTES) {
          setUploadImageErrorText("imageSizeLimitText-string");
          setShowingUploadImageError(true);
          coach.setProperty("isLogoUploading", false);
          return;
        }

        const imageUrl = URL.createObjectURL(selectedImage);
        const img = new window.Image();
        img.src = imageUrl;
        img.onload = () => {
          coach.setProperty("logoAspectRatioWH", img.width / img.height);
          coach.setProperty("logoAspectRatioHW", img.height / img.width);
          URL.revokeObjectURL(imageUrl);
        };

        handleUpload(selectedImage);
      }
    };

    return (
      <View style={{ alignItems: "center", flexDirection: "row" }}>
        {coach.isLogoUploading ? (
          <View
            style={{
              height: screenSize === "small" ? 25 : 50,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CustomActivityIndicator large={false} white={false} />
          </View>
        ) : coach.logoUrl ? (
          <View style={{ alignItems: "center" }}>
            <LogoImage coach={coach} />
            <View style={{ flexDirection: "row", marginTop: 10 }}>
              <Text
                style={{ marginRight: 20, color: Colors.grayColor }}
                onPress={() =>
                  !coach.isOffline &&
                  document.getElementById("fileInput").click()
                }
              >
                {t("change-string")}
              </Text>
              <Text
                style={{ color: "rgb(240, 68, 56)" }}
                onPress={() =>
                  !coach.isOffline && setShowingDeleteLogoWarning(true)
                }
              >
                {t("delete-string")}
              </Text>
            </View>
          </View>
        ) : (
          <TouchableOpacity
            onPress={() =>
              !coach.isOffline && document.getElementById("fileInput").click()
            }
            style={{
              borderRadius: 20,
              borderWidth: 1,
              borderColor: Colors.grayColor,
              borderStyle: "dashed",
              padding: 20,
              justifyContent: "center",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Text
              style={{
                color: Colors.grayColor,
                textAlign: "center",
                fontWeight: isHovered ? "500" : "normal",
              }}
            >
              {t("uploadYourLogo-string")}
            </Text>
          </TouchableOpacity>
        )}
        <input
          type="file"
          id="fileInput"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageChange}
        />
      </View>
    );
  });

  const LogOutButton = () => (
    <HoverableButton
      onPress={logout}
      style={{
        backgroundColor: Colors.whiteColor,
        height: 40,
        paddingHorizontal: 10,
        borderRadius: 14,
        borderWidth: 0.1,
        borderColor: "rgba(0,0,0,0.2)",
        alignItems: "center",
        justifyContent: "space-around",
      }}
      disabled={coach.isOffline}
    >
      {(isHovered) => (
        <View
          style={[
            GlobalStyles.HStack,
            { marginHorizontal: 10, opacity: isHovered ? 0.7 : 1 },
          ]}
        >
          <Feather name="log-out" size={18} color="black" />
          <Text style={[Fonts.medium14, { marginLeft: 4 }]}>
            {t("logOut-string")}
          </Text>
        </View>
      )}
    </HoverableButton>
  );

  const DeleteAccountButton = () => (
    <HoverableButton
      onPress={() => setShowingDeleteAccount(true)}
      style={{
        borderRadius: 14,
        marginTop: screenSize === "small" ? 24 : 32,
        marginBottom: 20,
        alignItems: "flex-start",
        justifyContent: "space-around",
      }}
      disabled={coach.isOffline}
    >
      {(isHovered) => (
        <Text
          style={[
            Fonts.medium14,
            {
              color: "rgb(240, 68, 56)",
              fontWeight: isHovered ? "600" : "normal",
            },
          ]}
        >
          {t("deleteAccount-string")}
        </Text>
      )}
    </HoverableButton>
  );

  const SaveButton = observer(() => {
    const hasChanges = hasSettingsChanged();

    return (
      <HoverableButton
        onPress={saveCoachProfile}
        style={{
          height: 40,
          borderRadius: 14,
          backgroundColor: Colors.bubbleColor,
          borderWidth: 0.1,
          borderColor: "rgba(0, 0, 0,0.2)",
          width: 80,
          alignItems: "center",
          justifyContent: "space-around",
        }}
        disabled={!hasChanges || coach.isOffline}
      >
        {coach.isLoading ? (
          <CustomActivityIndicator large={false} white={true} />
        ) : (
          <Text style={[Fonts.medium14, { color: Colors.whiteColor }]}>
            {t("save-string")}
          </Text>
        )}
      </HoverableButton>
    );
  });

  // Main render
  return (
    <View style={styles.container}>
      <View
        style={[
          GlobalStyles.HStack,
          {
            alignItems: "center",
            marginBottom: screenSize === "small" ? 8 : 12,
            justifyContent: "space-between",
          },
        ]}
      >
        <Text style={Fonts.bold24}>{t("settings-string")}</Text>
        <SaveButton />
      </View>

      {coach && (
        <>
          <SettingsSection title="profile-string" topMargin={false}>
            <SettingsRow label={t("name-string")}>
              <View
                style={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "space-around",
                }}
              >
                <TextName firestoreProperty="name" placeholder="name-string" />
                <View style={{ marginVertical: 4 }} />
                <TextName
                  firestoreProperty="surname"
                  placeholder="surname-string"
                />
              </View>
            </SettingsRow>

            <SettingsDivider />

            <SettingsRow label={t("email-string")}>
              <UnmutableTextName
                property="email"
                placeholder="entrenador@gmail.com"
              />
            </SettingsRow>

            <SettingsDivider />

            <SettingsRow label={t("yourLogo-string")} isLast>
              <ImageUploadComponent />
            </SettingsRow>
          </SettingsSection>

          <SettingsSection title="preferences-string">
            <SettingsRow
              label={t("onboardingPreferenceMeasurementUnit-string")}
            >
              <BinaryPicker
                firestoreProperty="exercise_unit"
                option1Text={t("onboardingPreferenceKg-string")}
                option2Text={t("onboardingPreferenceLbs-string")}
                getValue={(index) => (index === 0 ? false : true)}
              />
            </SettingsRow>

            <SettingsDivider />

            <SettingsRow
              label={t("onboardingPreferenceIntensityRating-string")}
              tooltip="onboardingPreferenceIntensityRatingTooltip-string"
            >
              <BinaryPicker
                firestoreProperty="rir_or_rpe"
                option1Text={t("onboardingPreferenceRPE-string")}
                option2Text={t("onboardingPreferenceRIR-string")}
                getValue={(index) => (index === 0 ? false : true)}
              />
            </SettingsRow>

            <SettingsDivider />

            <SettingsRow
              label={t("onboardingPreferenceShowExerciseStats-string")}
              tooltip="onboardingPreferenceShowExerciseStatsTooltip-string"
            >
              <BinaryPicker
                firestoreProperty="show_athlete_exercise_stats"
                option1Text={t("onboardingPreferenceShow-string")}
                option2Text={t("onboardingPreferenceHide-string")}
              />
            </SettingsRow>

            <SettingsDivider />

            <SettingsRow
              label={t("onboardingPreferenceShowPriority-string")}
              tooltip="onboardingPreferenceShowPriorityTooltip-string"
            >
              <BinaryPicker
                firestoreProperty="show_set_priority"
                option1Text={t("onboardingPreferenceShow-string")}
                option2Text={t("onboardingPreferenceHide-string")}
              />
            </SettingsRow>

            <SettingsDivider />

            <SettingsRow
              label={t("preferenceDefaultBlockView-string")}
              tooltip="preferenceDefaultBlockViewTooltip-string"
            >
              <BinaryPicker
                firestoreProperty="default_block_view"
                option1Text={t("workouts-string")}
                option2Text={t("dashboard-string")}
                getValue={(index) =>
                  index === 0 ? "workouts-string" : "dashboard-string"
                }
              />
            </SettingsRow>

            {shouldShowLegacyBlockViewSettings && (
              <>
                <SettingsDivider />
                <SettingsRow label={t("legacyBlockViewSettingsTitle-string")}>
                  <LegacyViewPicker />
                </SettingsRow>
              </>
            )}

            <SettingsDivider />

            <SettingsRow label={t("language-string")} isLast>
              <BinaryPicker
                firestoreProperty="coach_language"
                option1Text="ES"
                option2Text="EN"
                getValue={(index) => (index === 0 ? "es" : "en")}
              />
            </SettingsRow>
          </SettingsSection>

          <View
            style={[
              styles.settingsCard,
              { marginTop: screenSize === "small" ? 8 : 12 },
            ]}
          >
            <SettingsRow label={t("resetPredefinedExercises-string")}>
              <ActionButton
                onPress={() => setShowingResetExercises(true)}
                label="resetButton-string"
              />
            </SettingsRow>

            <SettingsDivider />

            <SettingsRow label={t("resetPredefinedAttributes-string")} isLast>
              <ActionButton
                onPress={() => setShowingResetAttributes(true)}
                label="resetButton-string"
              />
            </SettingsRow>
          </View>

          <SettingsSection title="learnAndUpdates-string">
            <SettingsRow
              label={t("tutorials-string")}
              hasDescription
              description={t("learnToUseEfortCoach-string")}
              noTranslateDescription={true}
            >
              <ActionButton
                onPress={() => openURL(getDocsURL())}
                label="viewTutorials-string"
              />
            </SettingsRow>

            <SettingsDivider />

            <SettingsRow
              label={t("whatsNew-string")}
              hasDescription
              description={t("currentVersion-string", {
                version: getFormattedVersion(),
              })}
              noTranslateDescription={true}
              isLast
            >
              <ActionButton
                onPress={() => coach.setProperty("showingWhatsNew", true)}
                label="viewWhatsNew-string"
              />
            </SettingsRow>
          </SettingsSection>

          <SettingsSection title="support-string">
            <SettingsRow
              label={t("reportProblem-string")}
              hasDescription
              description={t("reportProblemText-string")}
            >
              <ActionButton
                onPress={() => {
                  if (coach.email == "efortapp@gmail.com") {
                    coach.clientsDemoData("B2Xm8CFPyIS2taVlusbcIicWItF3");
                  } else {
                    openURL(INSTAGRAM_URL);
                  }
                }}
                label="contactUs-string"
              />
            </SettingsRow>

            <SettingsDivider />

            <SettingsRow label={t("sendFeedbackSettings-string")} isLast>
              <ActionButton
                onPress={() => {
                  if (coach.email == "efortapp@gmail.com") {
                    coach.clientsDemoData(
                      "B2Xm8CFPyIS2taVlusbcIicWItF3",
                      false
                    );
                  } else {
                    coach.setProperty("showingSendFeedback", true);
                  }
                }}
                label="sendFeedbackSettings-string"
              />
            </SettingsRow>
          </SettingsSection>

          {coach.subscriptionStatus === "active" && !coach.allowedAccount && (
            <SettingsSection title="subscription-string">
              <SettingsRow label={t("manageSubscription-string")} isLast>
                <ActionButton
                  onPress={() => openURL(STRIPE_BILLING_URL)}
                  label="manage-string"
                />
              </SettingsRow>
              {coach.clients.length === coach.maxClients && (
                <Text
                  style={{
                    width: "100%",
                    textAlign: "left",
                    marginTop: 20,
                    color: "gray",
                  }}
                >
                  {t("useUpgradeDiscount-string")}
                </Text>
              )}
            </SettingsSection>
          )}

          <Text
            style={[
              Fonts.medium20,
              {
                marginBottom: screenSize === "small" ? 8 : 12,
                paddingTop: screenSize === "small" ? 16 : 24,
              },
            ]}
          >
            {t("account-string")}
          </Text>

          <View style={GlobalStyles.HStack}>
            <LogOutButton />
          </View>
          <DeleteAccountButton />

          <BlockViewRevertModal
            isVisible={showingLegacyViewModal}
            onClose={() => setShowingLegacyViewModal(false)}
            onRevert={handleConfirmEnableLegacyView}
            coach={coach}
          />

          {/* Alerts */}
          <CustomAlert
            isVisible={showingResetExercises}
            onClose={() => setShowingResetExercises(false)}
            title={ALERT_CONFIGS.resetExercises.title}
            text={ALERT_CONFIGS.resetExercises.text}
            action={resetPredefinedExercises}
            specialCTA={ALERT_CONFIGS.resetExercises.cta}
          />
          <CustomAlert
            isVisible={showingResetAttributes}
            onClose={() => setShowingResetAttributes(false)}
            title={ALERT_CONFIGS.resetAttributes.title}
            text={ALERT_CONFIGS.resetAttributes.text}
            action={resetPredefinedAttributes}
            specialCTA={ALERT_CONFIGS.resetAttributes.cta}
          />
          <CustomAlert
            isVisible={showingDeleteAccount}
            onClose={() => setShowingDeleteAccount(false)}
            title={ALERT_CONFIGS.deleteAccount.title}
            text={ALERT_CONFIGS.deleteAccount.text}
            action={deleteAllClientsAndCoach}
            specialCTA={ALERT_CONFIGS.deleteAccount.cta}
          />
          <CustomAlert
            isVisible={showingUploadImageError}
            onClose={() => setShowingUploadImageError(false)}
            title={ALERT_CONFIGS.uploadError.title}
            text={uploadImageErrorText}
            specialCTA={ALERT_CONFIGS.uploadError.cta}
          />
          <CustomAlert
            isVisible={showingDeleteLogoWarning}
            onClose={() => setShowingDeleteLogoWarning(false)}
            title={ALERT_CONFIGS.deleteLogo.title}
            text={ALERT_CONFIGS.deleteLogo.text}
            action={deleteLogo}
            specialCTA={ALERT_CONFIGS.deleteLogo.cta}
          />
        </>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  settingsCard: {
    backgroundColor: Colors.whiteColor,
    padding: 20,
    borderRadius: 14,
    borderWidth: 0.1,
    borderColor: "rgba(0,0,0,0.2)",
  },
  divider: {
    borderColor: "rgba(0,0,0,0.2)",
    opacity: 0.5,
    flex: 1,
    borderRadius: 14,
    borderWidth: 0.1,
    marginBottom: 20,
  },
  textInputStyle: {
    height: 40,
  },
  textInputContainer: {
    borderRadius: 12,
    borderColor: "rgba(0,0,0,0.2)",
    borderWidth: 0.1,
    padding: 10,
    height: 40,
  },
  segmentedControl: {
    flexDirection: "row",
    justifyContent: "center",
    borderRadius: 12,
    alignSelf: "center",
    alignContent: "center",
    alignItems: "center",
  },
  segment: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  selectedSegment: {
    backgroundColor: "rgba(63,123,141,0.9)",
    borderColor: "rgba(63,123,141,0.9)",
  },
  containerPicker: {
    backgroundColor: Colors.backgroundColor,
    borderRadius: 12,
  },
  resetButton: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.bubbleColor,
  },
  tooltipIconContainer: {
    marginLeft: 8,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default CoachSettings;
